import {
  asString,
  parseButton,
  parseList,
  parseOptional,
  parseStatusBar,
  parseStatusLine,
  parseStatusTag
} from 'services/homeProducts/homeProducts.mappers';

import { ProgramProductCard, ProgramProductCardItem } from './programProducts.types';

/**
 * Defensive parsers for the `GET /pages/programs/:programId/products` payload.
 *
 * Same forward-compatibility rules as the Home feed — unknown fields ignored, server order kept, a
 * malformed entity dropped rather than failing the whole feed, unknown `style` resolved to a neutral tone
 * at render time. The **slot** parsers are shared with Home; only these card/row shapes are program-owned.
 */

/** A row needs both `id` and `title` — without them there is nothing to render, so it is dropped. */
const parseItem = (json: Record<string, unknown>): ProgramProductCardItem | undefined => {
  const id = asString(json.id);
  const title = asString(json.title);
  if (id === undefined || title === undefined) return undefined;

  return {
    id,
    image: asString(json.image),
    link: asString(json.link),
    statusTag: parseOptional(json.statusTag, parseStatusTag),
    subtitle: asString(json.subtitle),
    title
  };
};

/**
 * A card needs an `id` and at least one well-formed row. An empty (or entirely malformed) `items[]` leaves
 * nothing to show, so the card is dropped — there is no card-level identity to fall back on.
 *
 * Note there is no `link`: the card body is never a tap target.
 */
const parseCard = (json: Record<string, unknown>): ProgramProductCard | undefined => {
  const id = asString(json.id);
  if (id === undefined) return undefined;

  const items = parseList(json.items, parseItem);
  if (!items.length) return undefined;

  return {
    button: parseOptional(json.button, parseButton),
    id,
    items,
    statusBar: parseOptional(json.statusBar, parseStatusBar),
    statusLine: parseOptional(json.statusLine, parseStatusLine)
  };
};

/** Normalizes the raw flat `items` payload into ordered, well-formed program cards. */
export const normalizeProgramCards = (items: unknown): ProgramProductCard[] =>
  parseList(items, parseCard);
